"use client";

import React, { useState, useEffect } from "react";
import { Search, Lightbulb, Rocket, TrendingUp } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const steps = [{
  id: "discovery",
  title: "Identifying Community Needs",
  description: "We work closely with local leaders, schools, and community members in Ghana to understand the real educational challenges children and families are facing.",
  icon: Search,
  isLargeIcon: false
}, {
  id: "planning",
  title: "Planning the Right Support",
  description: "After understanding the needs, we carefully plan projects and support initiatives that will make a practical and meaningful difference in the community.",
  icon: Lightbulb,
  isLargeIcon: false
}, {
  id: "delivery",
  title: "Delivering the Project",
  description: "We put plans into action by providing educational resources, improving learning environments, and supporting projects that directly benefit children and schools.",
  icon: Rocket,
  isLargeIcon: true
}, {
  id: "impact",
  title: "Creating Lasting Impact",
  description: "We continue to review our work so that every project brings sustainable value and long-term benefit to the communities we serve.",
  icon: TrendingUp,
  isLargeIcon: false
}];
export default function TheConversionFramework() {
  const [activeIndex, setActiveIndex] = useState(0);
  const progressValue = (activeIndex + 1) / steps.length * 100;
  return <section data-section-id="135600" id="our-methodology" className="relative overflow-hidden bg-background py-12 sm:py-16 md:py-20 lg:py-24 text-foreground">
      <div className="sticky top-0 z-40 w-full border-b border-border/50 bg-background/90 px-4 py-3 backdrop-blur-md supports-[backdrop-filter]:bg-background/60">
        <div className="container mx-auto flex max-w-5xl items-center gap-3 sm:gap-4 px-0 sm:px-4">
          <span className="whitespace-nowrap text-[11px] sm:text-sm font-bold uppercase tracking-widest text-primary">
            Our Process
          </span>
          <Progress value={progressValue} className="h-2 flex-1" />
        </div>
      </div>

      <div className="container mx-auto max-w-5xl px-4 pt-10 sm:pt-12 md:pt-16">
        <div className="mb-12 text-center sm:mb-16 md:mb-20">
          <h2 className="mb-4 sm:mb-5 md:mb-6 text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight">
            How We <span className="text-primary">Work</span>
          </h2>
          <p className="mx-auto max-w-2xl text-base sm:text-lg md:text-xl font-medium text-muted-foreground leading-7 sm:leading-8">
            Our approach helps us turn support and donations into practical
            action that improves education and opportunities for children and
            communities in Ghana.
          </p>
        </div>

        <div className="relative">
          <div className="absolute top-8 bottom-8 left-[31px] z-0 w-px border-l-2 border-dashed border-primary/20 md:left-1/2 md:-translate-x-1/2" />

          <div className="space-y-12 sm:space-y-14 md:space-y-20 lg:space-y-24">
            {steps.map((step, index) => <StepRow key={step.id} step={step} index={index} activeIndex={activeIndex} setActiveIndex={setActiveIndex} />)}
          </div>
        </div>
      </div>
    </section>;
}
interface StepRowProps {
  step: (typeof steps)[0];
  index: number;
  activeIndex: number;
  setActiveIndex: (index: number) => void;
}
function StepRow({
  step,
  index,
  activeIndex,
  setActiveIndex
}: StepRowProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.6,
    once: false
  });
  const isEven = index % 2 === 0;
  const isActive = activeIndex >= index;
  const isCurrentlyActive = activeIndex === index;
  useEffect(() => {
    if (isVisible) {
      setActiveIndex(index);
    }
  }, [isVisible, index, setActiveIndex]);
  return <div ref={ref} data-index={index} className={cn("relative z-10 flex flex-row items-start gap-4 sm:gap-6 transition-all duration-700 md:items-center md:gap-0", isVisible ? "translate-y-0 opacity-100" : "translate-y-12 opacity-30")}>
      <div className={cn("hidden flex-1 justify-end px-8 md:flex", !isEven && "invisible")}>
        {isEven && <StepCard step={step} isActive={isActive} isCurrentlyActive={isCurrentlyActive} />}
      </div>

      <div className="relative flex w-16 shrink-0 justify-center">
        <div className={cn("z-10 flex size-14 sm:size-16 items-center justify-center rounded-full border-2 bg-background transition-all duration-500", isActive ? "border-primary shadow-[0_0_25px_rgba(var(--primary),0.3)]" : "border-muted/30", isCurrentlyActive && "scale-110 border-primary")}>
          <step.icon className={cn("transition-colors duration-500", step.isLargeIcon ? "size-7 sm:size-8" : "size-5 sm:size-6", isActive ? "text-primary" : "text-muted-foreground")} />
        </div>
      </div>

      <div className="w-full flex-1 md:px-8">
        <div className={cn(isEven ? "md:hidden" : "block")}>
          <StepCard step={step} isActive={isActive} isCurrentlyActive={isCurrentlyActive} />
        </div>
      </div>
    </div>;
}
interface StepCardProps {
  step: (typeof steps)[0];
  isActive: boolean;
  isCurrentlyActive: boolean;
}
function StepCard({
  step,
  isActive,
  isCurrentlyActive
}: StepCardProps) {
  return <Card className={cn("rounded-xl border-border/40 bg-card/90 backdrop-blur-sm transition-all duration-500", isActive ? "border-primary/40 shadow-lg shadow-primary/5" : "opacity-60 grayscale-[40%]", isCurrentlyActive && "scale-[1.02] border-primary shadow-2xl shadow-primary/20 grayscale-0")}>
      <CardHeader className="pb-3 sm:pb-4">
        <CardTitle className={cn("text-lg sm:text-xl md:text-2xl font-bold tracking-tight transition-colors duration-500", isActive ? "text-foreground" : "text-muted-foreground")}>
          {step.title}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="font-medium leading-7 text-sm sm:text-base text-muted-foreground">
          {step.description}
        </p>
      </CardContent>
    </Card>;
}